import React, { useState } from 'react';
import styled from 'styled-components';
import yaml from 'js-yaml';

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const Modal = styled.div`
  background: white;
  border-radius: 8px;
  width: 700px;
  max-width: 90%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const Header = styled.div`
  padding: 20px;
  border-bottom: 1px solid #e1e4e8;
`;

const Title = styled.h2`
  margin: 0 0 8px 0;
  font-size: 18px;
  color: #24292f;
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 13px;
  color: #586069;
`;

const Content = styled.div`
  padding: 20px;
  overflow-y: auto;
  flex: 1;
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #24292f;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d0d7de;
  border-radius: 6px;
  font-size: 14px;
  margin-bottom: 16px;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: #0969da;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px;
  border: 1px solid #d0d7de;
  border-radius: 6px;
  font-size: 13px;
  font-family: 'Monaco', 'Menlo', monospace;
  resize: vertical;
  min-height: 200px;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: #0969da;
  }
`;

const ErrorMessage = styled.div`
  padding: 12px;
  background: #fff1f0;
  border: 1px solid #ffccc7;
  border-radius: 6px;
  color: #cf222e;
  font-size: 13px;
  margin-bottom: 16px;
`;

const PreviewBox = styled.div`
  padding: 12px;
  background: #f6f8fa;
  border: 1px solid #d0d7de;
  border-radius: 6px;
  font-size: 13px;
  font-family: 'Monaco', 'Menlo', monospace;
  white-space: pre;
  overflow-x: auto;
  max-height: 300px;
  overflow-y: auto;
`;

const Footer = styled.div`
  padding: 16px 20px;
  border-top: 1px solid #e1e4e8;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const Button = styled.button`
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  border: 1px solid ${props => props.$primary ? '#0969da' : '#d0d7de'};
  background: ${props => props.$primary ? '#0969da' : 'white'};
  color: ${props => props.$primary ? 'white' : '#24292f'};
  
  &:hover {
    background: ${props => props.$primary ? '#0860ca' : '#f6f8fa'};
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

function inferSchema(obj) {
  if (obj === null) return { type: 'null' };
  if (Array.isArray(obj)) {
    if (obj.length === 0) return { type: 'array', items: {} };
    return { type: 'array', items: inferSchema(obj[0]) };
  }
  
  const type = typeof obj;
  
  if (type === 'object') {
    const properties = {};
    const required = [];
    
    for (const key in obj) {
      properties[key] = inferSchema(obj[key]);
      required.push(key);
    }
    
    return {
      type: 'object',
      properties,
      required
    };
  }
  
  if (type === 'number') {
    return Number.isInteger(obj) ? { type: 'integer' } : { type: 'number' };
  }
  
  if (type === 'boolean') return { type: 'boolean' };
  if (type === 'string') return { type: 'string' };
  
  return {};
}

function JsonToSchemaModal({ spec, onSave, onClose }) {
  const [schemaName, setSchemaName] = useState('');
  const [jsonInput, setJsonInput] = useState('');
  const [error, setError] = useState('');
  const [schema, setSchema] = useState(null);

  const handleJsonChange = (value) => {
    setJsonInput(value);
    setError('');
    
    if (!value.trim()) {
      setSchema(null);
      return;
    }
    
    try {
      const parsed = JSON.parse(value);
      const inferred = inferSchema(parsed);
      setSchema(inferred);
    } catch (e) {
      setError('Invalid JSON: ' + e.message);
      setSchema(null);
    }
  };

  const handleSave = () => {
    if (!schemaName.trim()) {
      setError('Schema name is required');
      return;
    }
    
    if (!schema) {
      setError('Valid JSON is required');
      return;
    }

    try {
      const parsed = yaml.load(spec);
      
      if (!parsed.components) parsed.components = {};
      if (!parsed.components.schemas) parsed.components.schemas = {};
      
      parsed.components.schemas[schemaName] = schema;
      
      const updated = yaml.dump(parsed, { indent: 2, lineWidth: -1 });
      onSave(updated);
    } catch (e) {
      setError('Failed to update spec: ' + e.message);
    }
  };

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>🔄 JSON to Schema</Title>
          <Subtitle>Paste a JSON object to automatically generate an OpenAPI schema</Subtitle>
        </Header>
        <Content>
          <Label>Schema Name</Label>
          <Input
            type="text"
            placeholder="e.g., User, Product, Order"
            value={schemaName}
            onChange={(e) => setSchemaName(e.target.value)}
          />
          
          <Label>JSON Object</Label>
          <TextArea
            placeholder={'{\n  "id": 1,\n  "name": "John Doe",\n  "email": "john@example.com",\n  "active": true\n}'}
            value={jsonInput}
            onChange={(e) => handleJsonChange(e.target.value)}
          />
          
          {error && <ErrorMessage>{error}</ErrorMessage>}
          
          {schema && (
            <>
              <Label>Generated Schema Preview</Label>
              <PreviewBox>{yaml.dump(schema, { indent: 2, lineWidth: -1 })}</PreviewBox>
            </>
          )}
        </Content>
        <Footer>
          <Button onClick={onClose}>Cancel</Button>
          <Button $primary onClick={handleSave} disabled={!schema || !schemaName.trim()}>
            Add Schema
          </Button>
        </Footer>
      </Modal>
    </Overlay>
  );
}

export default JsonToSchemaModal;
